import React, { useRef, useState } from "react";
import { Calendar, SlotInfo, momentLocalizer } from "react-big-calendar";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Box } from "@mui/material";
import useStyles from "./timesheet-calendar.style";
import {
  MyTimesheetCalendarDateHeader,
  MyTimesheetCalendarDayHeader,
  MyTimesheetCalendarEventWrapper,
  MyTimesheetCalendarMonthEvent,
  MyTimesheetCalendarToolbar,
  MyTimesheetCalendarWeeklyEvent,
  MyTimesheetCalendarWeeklyHeader,
} from "my-timesheet/components";
import { useOcContractCalendar, useOcCurrentUserState } from "oc/hooks";

type MyTimesheetTimesheetsCalendarProps = {
  data: any[];
  currentDate: Date;
  onChangeCurrentDate: (date: Date) => void;
  onShowRequest: (id: string) => void;
  onDataFormatChange: (format: string) => void;
  onNewRequest: (date: Date) => void;
};

export const MyTimesheetTimesheetsCalendar = ({
  data,
  currentDate,
  onChangeCurrentDate,
  onShowRequest,
  onDataFormatChange,
  onNewRequest,
}: MyTimesheetTimesheetsCalendarProps) => {
  const [currentView, setCurrentView] = useState<any>(null);

  const classes = useStyles();

  let { contractCode } = useOcCurrentUserState();

  const startDate = moment(currentDate).startOf("year").toDate();
  const endDate = moment(currentDate).endOf("year").toDate();

  const { contractCalendar } = useOcContractCalendar({
    contract: contractCode,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  });

  const customBackground = (date: Date) => {
    if (date && contractCalendar) {
      const day = moment(date).dayOfYear();
      const type = contractCalendar[day - 1];
      if (type === "P") {
        return "rbc-weekend-day";
      }
      if (type === "F") {
        return "rbc-payed-holiday-day";
      }
    }
    return "";
  };

  let calendarRef = useRef<any>(null);
  const { t, i18n } = useTranslation();
  const localizer = momentLocalizer(moment);
  (localizer as any).segmentOffset = 0;

  const culture = i18n.language === "hu" ? "hu" : "en";
  let messages = {
    date: t("OC_CALENDAR_DATE"),
    time: t("OC_CALENDAR_TIME"),
    event: t("OC_CALENDAR_EVENT"),
    allDay: t("OC_CALENDAR_ALL_DAY"),
    week: t("OC_CALENDAR_WEEK"),
    work_week: t("OC_CALENDAR_WORK_WEEK"),
    day: t("OC_CALENDAR_DAY"),
    month: t("OC_CALENDAR_MONTH"),
    previous: t("OC_CALENDAR_PREVIOUS"),
    next: t("OC_CALENDAR_NEXT"),
    yesterday: t("OC_CALENDAR_YESTERDAY"),
    tomorrow: t("OC_CALENDAR_TOMORROW"),
    today: t("OC_TODAY"),
    agenda: t("OC_CALENDAR_AGENDA"),
    noEventsInRange: t("OC_CALENDAR_NO_EVENTS_IN_RANGE"),
    showMore: (total: number) => `+${total} ${t("OC_CALENDAR_MORE")}`,
  };

  const eventPropGetter = (event: any) => {
    let actualClass = "";

    if (event?.state === "approved" && event?.id.startsWith("leave-")) {
      actualClass = "rbc-my-sheet-event-recorded";
    } else {
      switch (event?.state) {
        case "requested":
          actualClass = "rbc-my-sheet-event-requested";
          break;
        case "approved":
          actualClass = "rbc-my-sheet-event-approved";
          break;
        case "recorded":
          actualClass = "rbc-my-sheet-event-recorded";
          break;
        case "rejected":
          actualClass = "rbc-my-sheet-event-rejected";
          break;
        default:
          actualClass = "rbc-my-sheet-event";
          break;
      }
    }
    return {
      className: actualClass,
    };
  };

  const handleSelectSlot = (slotInfo: SlotInfo) => {
    onNewRequest(slotInfo?.start);
  };

  return (
    <Box
      style={currentView === "week" ? { height: "auto" } : { height: "900px" }}
      className={classes.root}
    >
      <Calendar
        ref={calendarRef}
        date={currentDate}
        onNavigate={(date) => {
          onChangeCurrentDate(date);
        }}
        onSelectEvent={(props) => {
          if (props?.id) {
            onShowRequest(props.id);
          }
        }}
        localizer={localizer}
        culture={culture}
        messages={messages}
        events={data || []}
        startAccessor="start"
        endAccessor="end"
        slotPropGetter={(d) => {
          return { className: customBackground(d as Date) };
        }}
        dayPropGetter={(d) => {
          return { className: customBackground(d as Date) };
        }}
        toolbar={true}
        tooltipAccessor={null}
        views={{ month: true, week: true }}
        // components-ben a nem szabványos kulcsok miatt cast
        components={
          {
            toolbar: MyTimesheetCalendarToolbar as any,
            month: {
              header: MyTimesheetCalendarDayHeader,
              dateHeader: MyTimesheetCalendarDateHeader,
              event: MyTimesheetCalendarMonthEvent as any,
              eventWrapper: MyTimesheetCalendarEventWrapper as any,
            },
            week: {
              header: MyTimesheetCalendarWeeklyHeader,
              event: MyTimesheetCalendarWeeklyEvent,
            },
          } as any
        }
        eventPropGetter={eventPropGetter}
        showMultiDayTimes={true}
        onView={(view: any) => setCurrentView(view)}
        dayLayoutAlgorithm="overlap"
        selectable={true}
        onSelectSlot={handleSelectSlot}
        onShowMore={() => onDataFormatChange("OC_TABLE")}
      />
    </Box>
  );
};

export default MyTimesheetTimesheetsCalendar;
